import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, X, ChevronDown, ChevronUp, Star, Zap, Play } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { PACKAGES, MODULE_PRICES, CEREBRUM_PRICES, TIER_FEATURES, EXTRA_USER_PRICE, INTRO_MONTHS } from "../data/pricingData";
import PageMeta from "../components/PageMeta";
import { DEMO_URL } from "../lib/urls";

const TIERS = ["starter", "growth", "business"];

const MODULE_LIST = [
  { id: "sales-flow", name: "SalesFlow" },
  { id: "ops", name: "Ops" },
  { id: "teams", name: "Teams" },
  { id: "projects", name: "Projects" },
  { id: "stockroom", name: "Stockroom" },
  { id: "bookkeep", name: "Bookkeep" },
  { id: "support", name: "Support" },
  { id: "connect", name: "Connect" },
];

const FAQS = [
  { q: "What payment methods do you accept?", a: "Credit/debit cards and bank transfers. Multi-currency billing adjusts to your region automatically." },
  { q: "Can I cancel at any time?", a: "Yes. Cancel anytime — your access continues until the end of the billing cycle." },
  { q: "Is there a contract?", a: "No long-term contracts. Pay monthly or annually (annual saves 15%)." },
  { q: "Can I white-label Rogue?", a: "Yes — on Growth tier and above. Your logo, your brand, your domain." },
  { q: "What happens after the 3-month intro?", a: "Your plan continues at the standard price. No surprises — you'll see the price clearly before the intro ends." },
  { q: "Can I mix All Rogue and à la carte?", a: "The All Rogue package includes all 8 modules. If you only need 1-2 modules, à la carte is more cost-effective." },
  { q: "Can I switch tiers later?", a: "Absolutely. Upgrade or downgrade anytime — changes take effect on your next billing cycle." },
  { q: "Does VAT apply?", a: "All prices are exclusive of VAT. Applicable taxes are added based on your country." },
];

export default function RoguePricing({ onTrialClick }) {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedTier, setSelectedTier] = useState("growth");
  const [addCerebrum, setAddCerebrum] = useState(false);
  const [moduleCount, setModuleCount] = useState(8);
  const [userCount, setUserCount] = useState(5);
  const { symbol, rate } = useCurrency();

  const cx = (usd) => Math.round(usd * rate);
  const fmt = (usd) => `${symbol} ${cx(usd).toLocaleString()}`;
  const pkg = PACKAGES[selectedTier];
  const cerebrum = CEREBRUM_PRICES[selectedTier];

  // Calculator logic
  const isFullPackage = moduleCount === 8;
  const alaCarte = moduleCount * MODULE_PRICES[selectedTier] * userCount;
  const packagePrice = pkg.price + (addCerebrum ? cerebrum.price : 0);
  const packageIntro = pkg.intro + (addCerebrum ? cerebrum.intro : 0);
  const alaCarteWithCerebrum = alaCarte + (addCerebrum ? cerebrum.price : 0);
  const alaCarteIntro = Math.round(alaCarteWithCerebrum * 0.5);

  const displayPrice = isFullPackage ? packagePrice : alaCarteWithCerebrum;
  const displayIntro = isFullPackage ? packageIntro : alaCarteIntro;

  return (
    <div className="min-h-screen" data-testid="rogue-pricing-page">
      <PageMeta title="Rogue Pricing" description="Rogue pricing — per-module or all-inclusive. 50% off your first 3 months." path="/space/rogue/pricing" />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-8 md:pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <img src="/logos/rogue/white-blue.png" alt="Rogue" className="h-8 w-auto mb-4" />
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-white leading-tight mb-3">
            Simple pricing. No surprises.
          </h1>
          <p className="text-lg text-geminii-muted max-w-xl mb-6">
            All Rogue or pick your modules. 50% off your first {INTRO_MONTHS} months.
          </p>
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-hero-demo"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-rogue-cyan/30 text-rogue-cyan font-semibold text-sm hover:bg-rogue-cyan/10 hover:border-rogue-cyan/60 transition-all duration-250"
          >
            <Play size={13} className="fill-rogue-cyan" />
            View live demo — no signup
          </a>
        </div>
      </section>

      {/* Intro banner */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-rogue-cyan/10 border border-rogue-cyan/20 rounded-xl px-6 py-4 flex flex-wrap items-center gap-3" data-testid="pricing-promo-banner">
            <Zap size={16} className="text-rogue-cyan" />
            <span className="text-rogue-cyan font-bold text-sm">Launch offer: 50% off first {INTRO_MONTHS} months</span>
            <span className="text-geminii-muted text-sm">No credit card required to start. Cancel anytime.</span>
          </div>
        </div>
      </section>

      {/* ==================== BUILD YOUR PLAN (PRIMARY) ==================== */}
      <section className="py-8 md:py-12" data-testid="pricing-calculator">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-2">Build your plan</h2>
          <p className="text-geminii-muted mb-8 max-w-lg">Pick your tier, choose your modules, see your price — instantly.</p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Tier selector */}
              <div>
                <label className="text-xs font-medium text-geminii-muted mb-2 block">Select tier</label>
                <div className="flex gap-2">
                  {TIERS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setSelectedTier(t)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                        selectedTier === t
                          ? "bg-rogue-cyan text-geminii-deep"
                          : "bg-white/[0.04] text-geminii-muted border border-white/[0.06] hover:border-white/[0.12]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Module count */}
              <div>
                <label className="text-xs font-medium text-geminii-muted mb-2 block">How many modules?</label>
                <div className="flex gap-2 flex-wrap">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <button
                      key={n}
                      onClick={() => setModuleCount(n)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                        moduleCount === n
                          ? n === 8 ? "bg-rogue-cyan text-geminii-deep" : "bg-white/[0.1] text-white border border-white/[0.15]"
                          : "bg-white/[0.03] text-geminii-dim border border-white/[0.06]"
                      }`}
                    >
                      {n === 8 ? "All" : n}
                    </button>
                  ))}
                </div>
                {isFullPackage && <p className="text-xs text-rogue-cyan mt-2">All Rogue package — best value!</p>}
              </div>

              {/* User count */}
              {!isFullPackage && (
                <div>
                  <label className="text-xs font-medium text-geminii-muted mb-2 block">How many users?</label>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={userCount}
                    onChange={(e) => setUserCount(Number(e.target.value))}
                    className="w-full accent-rogue-cyan"
                  />
                  <div className="flex justify-between text-xs text-geminii-dim mt-1">
                    <span>1 user</span>
                    <span className="text-white font-medium">{userCount} users</span>
                    <span>50 users</span>
                  </div>
                </div>
              )}

              {/* Cerebrum toggle */}
              <button
                onClick={() => setAddCerebrum(!addCerebrum)}
                className="flex items-center gap-3 w-full p-4 rounded-lg bg-cerebrum-violet/5 border border-cerebrum-violet/15 text-left"
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center transition-all ${
                  addCerebrum ? "bg-cerebrum-violet" : "border border-white/[0.2]"
                }`}>
                  {addCerebrum && <Check size={14} className="text-white" />}
                </div>
                <span className="text-sm text-white font-medium">Add Cerebrum AI</span>
                <span className="ml-auto text-xs text-cerebrum-violet">
                  +{fmt(cerebrum.intro)}/mo
                </span>
              </button>
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 bg-geminii-card border border-rogue-cyan/20 rounded-xl p-6 shadow-[0_0_30px_-8px_rgba(31,182,193,0.15)]" data-testid="pricing-summary">
                <h3 className="font-display font-bold text-lg text-white mb-1">Your estimate</h3>
                <p className="text-xs text-geminii-dim mb-6 capitalize">{selectedTier} tier{isFullPackage ? " — All Rogue" : ` — ${moduleCount} module${moduleCount > 1 ? "s" : ""}`}{!isFullPackage ? ` × ${userCount} user${userCount > 1 ? "s" : ""}` : ""}</p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-geminii-muted">{isFullPackage ? "All Rogue package" : `${moduleCount} module${moduleCount > 1 ? "s" : ""} × ${userCount} user${userCount > 1 ? "s" : ""}`}</span>
                    <span className="text-geminii-dim line-through">{fmt(isFullPackage ? pkg.price : alaCarte)}</span>
                  </div>
                  {addCerebrum && (
                    <div className="flex justify-between text-sm">
                      <span className="text-cerebrum-violet/70">Cerebrum AI</span>
                      <span className="text-geminii-dim line-through">{fmt(cerebrum.price)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs text-rogue-emerald pt-2 border-t border-white/[0.06]">
                    <span>Intro discount (50% × {INTRO_MONTHS} months)</span>
                    <span>-{fmt(displayPrice - displayIntro)}</span>
                  </div>
                </div>

                <div className="flex justify-between items-end mb-1">
                  <span className="text-sm text-geminii-muted">First {INTRO_MONTHS} months</span>
                  <div className="text-right">
                    <span className="font-display font-bold text-3xl text-rogue-cyan" data-testid="pricing-total">
                      {fmt(displayIntro)}
                    </span>
                    <span className="text-xs text-geminii-dim block">/month</span>
                  </div>
                </div>
                <p className="text-[11px] text-geminii-dim mb-6">Then {fmt(displayPrice)}/mo after intro period</p>

                <button
                  onClick={onTrialClick}
                  data-testid="pricing-cta"
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_20px_rgba(31,182,193,0.4)] transition-all duration-250"
                >
                  Start free trial <ArrowRight size={14} />
                </button>
                <a
                  href={DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pricing-summary-demo"
                  className="mt-2 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/40 hover:text-rogue-cyan transition-all duration-250"
                >
                  <Play size={13} />
                  View live demo
                </a>
                <p className="text-[11px] text-geminii-dim text-center mt-3">No credit card required</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== ALL ROGUE PACKAGES ==================== */}
      <section className="py-8 md:py-12 border-t border-white/[0.04]" data-testid="pricing-packages">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-2">
            <Star size={18} className="text-rogue-cyan" />
            <h2 className="font-display font-bold text-2xl text-white">All Rogue — 8 modules, one price</h2>
          </div>
          <p className="text-geminii-muted mb-8 max-w-lg">
            The best value. Every module included. Just pick your team size.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier) => {
              const p = PACKAGES[tier];
              const tf = TIER_FEATURES[tier];
              return (
                <div
                  key={tier}
                  className={`relative bg-geminii-card border rounded-2xl p-7 transition-all ${
                    tf.popular ? "border-rogue-cyan/40 shadow-[0_0_30px_-8px_rgba(31,182,193,0.2)]" : "border-white/[0.06]"
                  }`}
                  data-testid={`package-${tier}`}
                >
                  {tf.popular && (
                    <span className="absolute -top-3 left-6 text-[10px] font-mono text-geminii-deep bg-rogue-cyan px-3 py-1 rounded-full font-bold">
                      Most popular
                    </span>
                  )}
                  <h3 className="font-display font-bold text-lg text-white mb-1">{p.name}</h3>
                  <p className="text-xs text-geminii-dim mb-5">{p.users}</p>

                  {/* Price with strikethrough */}
                  <div className="mb-1">
                    <span className="text-sm text-geminii-dim line-through mr-2">{fmt(p.price)}/mo</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-display font-bold text-4xl text-white">{fmt(p.intro)}</span>
                    <span className="text-sm text-geminii-dim">/mo</span>
                  </div>
                  <p className="text-[11px] text-rogue-cyan mb-6">
                    for the first {INTRO_MONTHS} months, then {fmt(p.price)}/mo
                  </p>

                  {/* Features */}
                  <ul className="space-y-2.5 mb-6">
                    {tf.included.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-geminii-muted">
                        <Check size={14} className="text-rogue-cyan mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                    {tf.excluded.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-geminii-dim/50">
                        <X size={14} className="mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={onTrialClick}
                    className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-250 ${
                      tf.popular
                        ? "bg-rogue-cyan text-geminii-deep hover:shadow-[0_0_20px_rgba(31,182,193,0.4)]"
                        : "border border-white/[0.12] text-white hover:border-rogue-cyan/50 hover:text-rogue-cyan"
                    }`}
                  >
                    Start free trial <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Enterprise */}
          <div className="mt-6 bg-geminii-card border border-white/[0.06] rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-base text-white">Enterprise (50+ users)</h3>
              <p className="text-sm text-geminii-dim">Custom pricing, SLA guarantees, dedicated infrastructure, on-premise option.</p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/[0.12] text-white text-sm font-medium hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all"
            >
              Contact sales <ArrowRight size={14} />
            </Link>
          </div>

          {/* Cerebrum add-on note */}
          <div className="mt-6 p-5 rounded-xl bg-cerebrum-violet/5 border border-cerebrum-violet/15">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono text-cerebrum-violet bg-cerebrum-violet/10 px-2 py-0.5 rounded">Add-on</span>
              <span className="text-sm font-medium text-white">Cerebrum AI</span>
            </div>
            <p className="text-xs text-geminii-muted">
              Add Cerebrum to any plan: <span className="line-through text-geminii-dim">{fmt(CEREBRUM_PRICES.growth.price)}/mo</span>{" "}
              <span className="text-cerebrum-violet font-semibold">{fmt(CEREBRUM_PRICES.growth.intro)}/mo</span> for the first {INTRO_MONTHS} months (Growth tier).
            </p>
          </div>
        </div>
      </section>

      {/* ==================== PER-MODULE À LA CARTE ==================== */}
      <section className="py-8 md:py-12 border-t border-white/[0.04]" data-testid="pricing-alacarte">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl text-white mb-2">Or pick individual modules</h2>
          <p className="text-geminii-muted mb-8 max-w-lg">
            Only need CRM? Or just field service? Buy exactly what you need.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="font-display font-semibold text-sm text-white pb-4 pr-6">Module</th>
                  {TIERS.map((t) => (
                    <th key={t} className="font-display font-semibold text-sm text-white pb-4 pr-6 text-center capitalize">{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULE_LIST.map((mod) => (
                  <tr key={mod.id} className="border-b border-white/[0.04]">
                    <td className="py-4 pr-6 text-sm text-white font-medium">{mod.name}</td>
                    {TIERS.map((t) => (
                      <td key={t} className="py-4 pr-6 text-center">
                        <span className="text-xs text-geminii-dim line-through mr-1">{fmt(MODULE_PRICES[t])}</span>
                        <span className="font-mono text-sm text-rogue-cyan font-medium">{fmt(Math.round(MODULE_PRICES[t] * 0.5))}</span>
                        <span className="text-[10px] text-geminii-dim block">/user/mo</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-geminii-dim mt-3">Intro pricing shown (first {INTRO_MONTHS} months at 50% off). Standard price struck through.</p>
        </div>
      </section>

      {/* ==================== CEREBRUM AI ADD-ON PRICING ==================== */}
      <section className="py-8 md:py-12 border-t border-white/[0.04]" data-testid="pricing-cerebrum">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] font-mono text-cerebrum-violet bg-cerebrum-violet/10 px-2 py-0.5 rounded">Add-on</span>
            <h2 className="font-display font-bold text-2xl text-white">Cerebrum AI · across the whole stack</h2>
          </div>
          <p className="text-geminii-muted mb-8 max-w-lg">
            One toggle. Adds AI insights, voice transcription, OCR, and predictive analytics to every module.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left max-w-2xl">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="font-display font-semibold text-sm text-white pb-4 pr-6">Tier</th>
                  <th className="font-display font-semibold text-sm text-geminii-dim pb-4 pr-6">Standard</th>
                  <th className="font-display font-semibold text-sm text-cerebrum-violet pb-4">First {INTRO_MONTHS} months</th>
                </tr>
              </thead>
              <tbody>
                {TIERS.map((tier) => (
                  <tr key={tier} className="border-b border-white/[0.04]">
                    <td className="py-4 pr-6 text-sm text-white font-medium capitalize">{tier}</td>
                    <td className="py-4 pr-6 font-mono text-sm text-geminii-dim line-through">{fmt(CEREBRUM_PRICES[tier].price)}/mo</td>
                    <td className="py-4 font-mono text-sm text-cerebrum-violet font-semibold">{fmt(CEREBRUM_PRICES[tier].intro)}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-10 md:py-16 border-t border-white/[0.04]" data-testid="pricing-faq">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl text-white mb-8">Frequently asked questions</h2>
          <div className="max-w-2xl space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-white/[0.06] rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  data-testid={`faq-${i}`}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="text-sm font-medium text-white pr-4">{faq.q}</span>
                  {expandedFaq === i ? (
                    <ChevronUp size={16} className="text-geminii-dim flex-shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-geminii-dim flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    className="px-5 pb-5"
                  >
                    <p className="text-sm text-geminii-muted leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
